import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrands } from '../lib/api/brands';
import { useCampaigns } from '../lib/api/campaigns';
import { BrandAvatar } from '../lib/brandAvatars';
import { Button } from '../components/ui/Button';
import { HowItWorksLink } from '../components/ui/HowItWorksLink';
import { Icon } from '../components/ui/Icon';
import CreateBrandModal from '../components/modals/CreateBrandModal';

const Brands = () => {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data: brands = [], isLoading } = useBrands();
  const { data: campaigns = [] } = useCampaigns();

  const campaignCountByBrand = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of campaigns) {
      for (const bid of c.brands) {
        m.set(bid, (m.get(bid) ?? 0) + 1);
      }
    }
    return m;
  }, [campaigns]);

  const visibleBrands = useMemo(() => {
    const q = query.trim().toLowerCase();
    let filtered = brands;
    if (q) filtered = filtered.filter((b) => b.name.toLowerCase().includes(q));
    // Priority (A/B/C) is stored but not shown for now (Doreen, 2026-10-12)
    // — plain alphabetical order until the rating is put to use.
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [brands, query]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Portfolio</div>
          <h1 className="page-title">Brand portfolio</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HowItWorksLink section="brands" />
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={13} /> New brand
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', maxWidth: 320, flex: '1 1 240px' }}>
          <span
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--ink-4)',
              pointerEvents: 'none',
            }}
          >
            <Icon name="search" size={14} />
          </span>
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brands…"
            style={{ paddingLeft: 32 }}
          />
        </div>
        <div className="filter-bar" style={{ margin: 0 }}>
          <span className="filter-pill active">All ({brands.length})</span>
        </div>
      </div>

      {isLoading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
          Loading brands…
        </div>
      ) : brands.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: 'var(--ink-3)', marginBottom: 12 }}>No brands yet.</p>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={13} /> Add your first brand
          </Button>
        </div>
      ) : visibleBrands.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
          No brands match "{query}".
        </div>
      ) : (
        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}
        >
          {visibleBrands.map((b) => {
            const count = campaignCountByBrand.get(b.id) ?? 0;
            return (
              <button
                key={b.id}
                type="button"
                className="card"
                onClick={() => navigate(`/brands/${b.id}`)}
                style={{
                  textAlign: 'left',
                  padding: 0,
                  background: 'var(--bg-elevated)',
                  cursor: 'pointer',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ padding: '18px 18px 16px', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <BrandAvatar brand={b} size={64} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                        {b.name}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                        {count} campaign{count === 1 ? '' : 's'}
                      </div>
                      {b.avg_margin && b.avg_margin.trim() && (
                        <div
                          style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}
                          title="Average gross margin at RRP across the brand's active catalog (auto)"
                        >
                          {b.avg_margin} margin
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <CreateBrandModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => navigate(`/brands/${id}`)}
      />
    </div>
  );
};

export default Brands;
